import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Page unique : création ET édition d'une fiche annuaire.
 * - Route `/creer-fiche-annuaire` → création
 * - Route `/annuaire/:type/:slug/edit` → édition
 */
export default function DirectoryEntryEditPage({ mode = "create" }) {
  const { type: routeType, slug } = useParams();
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [taxonomy, setTaxonomy] = useState(null);
  const [form, setForm] = useState({
    type: routeType === "sante-performance" ? "sante" : (routeType === "boutiques-services" ? "boutique" : "boutique"),
    category: "",
    subcategory: "",
    name: "",
    description: "",
    short_motto: "",
    disciplines: "",
    island: "",
    commune: "",
    address: "",
    photo_url: "",
    logo_url: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    social_facebook: "",
    social_instagram: "",
    social_tiktok: "",
    published: true,
  });
  const [entryId, setEntryId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/directory/taxonomy").then((res) => setTaxonomy(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !slug) return;
    api.get(`/directory/${slug}`).then((res) => {
      const e = res.data;
      setEntryId(e.id);
      setForm({
        type: e.type,
        category: e.category || "",
        subcategory: e.subcategory || "",
        name: e.name || "",
        description: e.description || "",
        short_motto: e.short_motto || "",
        disciplines: (e.disciplines || []).join(", "),
        island: e.island || "",
        commune: e.commune || "",
        address: e.address || "",
        photo_url: e.photo_url || "",
        logo_url: e.logo_url || "",
        contact_email: e.contact_email || "",
        contact_phone: e.contact_phone || "",
        website: e.website || "",
        social_facebook: e.social_facebook || "",
        social_instagram: e.social_instagram || "",
        social_tiktok: e.social_tiktok || "",
        published: e.published !== false,
      });
    });
  }, [mode, slug]);

  useEffect(() => {
    if (ready && !user) {
      toast.info("Connectez-vous pour continuer.");
      navigate("/login");
    }
  }, [ready, user, navigate]);

  const categories = (taxonomy && taxonomy[form.type]?.categories) || [];

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.name.trim() || !form.category) {
      toast.error("Le nom et la catégorie sont obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        disciplines: form.disciplines.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (mode === "edit" && entryId) {
        const res = await api.patch(`/directory/${entryId}`, payload);
        toast.success("Fiche mise à jour.");
        const typeSlug = res.data.type === "sante" ? "sante-performance" : "boutiques-services";
        navigate(`/annuaire/${typeSlug}/${res.data.slug}`);
      } else {
        const res = await api.post("/directory", payload);
        toast.success("Fiche créée avec succès.");
        const typeSlug = res.data.type === "sante" ? "sante-performance" : "boutiques-services";
        navigate(`/annuaire/${typeSlug}/${res.data.slug}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="mb-4">
        <Link to="/annuaire/boutiques-services" className="text-xs text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Retour à l'annuaire
        </Link>
      </div>
      <Card className="p-6">
        <h1 className="font-display font-bold text-2xl mb-1">
          {mode === "edit" ? "Modifier ma fiche" : "Référencer mon activité"}
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Créez ou modifiez votre présence dans l'annuaire AITO.PF. Gratuit et sans engagement.
        </p>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Type d'activité *</Label>
              <Select value={form.type} onValueChange={(v) => { update("type", v); update("category", ""); }}>
                <SelectTrigger data-testid="input-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sante">Santé & Performance</SelectItem>
                  <SelectItem value="boutique">Boutiques & Services</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger data-testid="input-category"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Nom ou raison sociale *</Label>
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-name" />
          </div>
          <div>
            <Label>Baseline courte</Label>
            <Input value={form.short_motto} onChange={(e) => update("short_motto", e.target.value)} placeholder="Ex : Votre kiné du sport à Tahiti" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={5} data-testid="input-description" />
          </div>
          <div>
            <Label>Disciplines liées (séparées par des virgules)</Label>
            <Input value={form.disciplines} onChange={(e) => update("disciplines", e.target.value)} placeholder="athletisme, va_a, surf" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Île</Label>
              <Input value={form.island} onChange={(e) => update("island", e.target.value)} placeholder="Tahiti" />
            </div>
            <div>
              <Label>Commune</Label>
              <Input value={form.commune} onChange={(e) => update("commune", e.target.value)} placeholder="Papeete" />
            </div>
          </div>
          <div>
            <Label>Adresse</Label>
            <Input value={form.address} onChange={(e) => update("address", e.target.value)} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Photo (URL ou base64)</Label>
              <Input value={form.photo_url} onChange={(e) => update("photo_url", e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label>Logo (URL ou base64)</Label>
              <Input value={form.logo_url} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://..." />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Email de contact</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Site internet</Label>
            <Input value={form.website} onChange={(e) => update("website", e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Facebook</Label>
              <Input value={form.social_facebook} onChange={(e) => update("social_facebook", e.target.value)} />
            </div>
            <div>
              <Label>Instagram</Label>
              <Input value={form.social_instagram} onChange={(e) => update("social_instagram", e.target.value)} />
            </div>
            <div>
              <Label>TikTok</Label>
              <Input value={form.social_tiktok} onChange={(e) => update("social_tiktok", e.target.value)} />
            </div>
          </div>

          {mode === "edit" && (
            <div className="flex items-center justify-between p-3 rounded-md bg-muted/30">
              <div>
                <div className="text-sm font-semibold">Fiche publiée</div>
                <div className="text-xs text-muted-foreground">Décochez pour repasser en brouillon.</div>
              </div>
              <Switch checked={form.published} onCheckedChange={(v) => update("published", v)} />
            </div>
          )}

          <Button className="w-full bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" disabled={saving} onClick={submit} data-testid="submit-directory-entry">
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "Enregistrement..." : (mode === "edit" ? "Enregistrer les modifications" : "Créer ma fiche")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
